import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { blockPermissionsService } from '../services/api';
import toast from 'react-hot-toast';

const BlockPermissions = ({ currentUser, onBack }) => {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const { data: permissionsData, isLoading, refetch } = useQuery({
    queryKey: ['block-permissions'],
    queryFn: async () => {
      const response = await blockPermissionsService.getPermissions();
      return response.data?.data || [];
    }
  });

  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    if (permissionsData) {
      setPermissions(permissionsData);
    }
  }, [permissionsData]);

  const handleToggle = (role, field) => {
    setPermissions(prev =>
      prev.map(p =>
        p.role === role ? { ...p, [field]: !p[field] } : p
      )
    );
  };

  const saveMutation = useMutation({
    mutationFn: async (perm) => {
      const response = await blockPermissionsService.updatePermission(perm.role, {
        can_create_blocks: perm.can_create_blocks,
        can_view_all_blocks: perm.can_view_all_blocks
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Permissões salvas com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao salvar permissão');
    }
  });

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      for (const perm of permissions) {
        await saveMutation.mutateAsync(perm);
      }
      queryClient.invalidateQueries(['block-permissions']);
      await refetch();
    } catch (err) {
      console.error('Erro ao salvar permissões:', err);
    } finally {
      setSaving(false);
    }
  };

  if (currentUser?.role !== 'GERENCIA') {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="text-center py-8 text-red-500">
          Você não tem permissão para acessar esta página.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">
          ⚙️ Permissões de Bloqueio de Agenda
        </h2>
        <button
          onClick={onBack}
          className="px-3 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          ← Voltar
        </button>
      </div>

      <p className="text-sm text-gray-600 mb-6">
        Configure quais roles podem criar bloqueios de agenda para si mesmos.
        Apenas Gerência pode criar bloqueios para outros usuários.
      </p>

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Carregando...</div>
      ) : (
        <>
          <div className="space-y-3">
            {permissions.map((perm) => (
              <div
                key={perm.role}
                className={`p-4 rounded-lg border-2 ${perm.role === 'GERENCIA'
                    ? 'bg-purple-50 border-purple-200'
                    : 'bg-gray-50 border-gray-200'
                  }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-gray-800">{perm.role}</span>
                    {perm.role === 'GERENCIA' && (
                      <span className="text-xs bg-purple-200 text-purple-800 px-2 py-0.5 rounded">
                        Sempre tem acesso
                      </span>
                    )}
                  </div>

                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={perm.can_create_blocks}
                        onChange={() => handleToggle(perm.role, 'can_create_blocks')}
                        disabled={perm.role === 'GERENCIA'}
                        className="w-5 h-5 text-blue-600 rounded"
                      />
                      <span className="text-sm text-gray-700">Criar bloqueios</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={perm.can_view_all_blocks}
                        onChange={() => handleToggle(perm.role, 'can_view_all_blocks')}
                        disabled={perm.role === 'GERENCIA'}
                        className="w-5 h-5 text-blue-600 rounded"
                      />
                      <span className="text-sm text-gray-700">Ver todos</span>
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-4 border-t">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Salvando...' : 'Salvar Permissões'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default BlockPermissions;